import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import QRCode from 'react-qr-code';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  Camera, Search, X, Edit2, Check, CreditCard,
  Banknote, Phone, MapPin, Tv, Calendar, AlertCircle, Tag, UserPlus, ScanText
} from 'lucide-react';
import { customerAPI, paymentAPI, configAPI } from '../services/api';

// Extract NUID candidates from OCR text
function extractNUIDCandidates(text) {
  const candidates = new Set();
  // 8–12 digit decimal sequences
  const decimals = text.match(/\b\d{8,12}\b/g) || [];
  decimals.forEach(n => candidates.add(n));
  // Hex patterns starting with 832b or 8317 (case-insensitive)
  const hexes = text.match(/\b(832[bB]|8317)[0-9a-fA-F]{4,8}\b/g) || [];
  hexes.forEach(h => candidates.add(h));
  return [...candidates];
}

const SCANNER_ID = 'stb-scanner';

// Normalize raw NUID from STB sticker barcode
function normalizeNUID(raw) {
  const s = raw.trim();
  // 12-digit decimal starting with 29–40 → keep first 10 digits
  if (/^\d{12}$/.test(s)) {
    const prefix = parseInt(s.substring(0, 2), 10);
    if (prefix >= 29 && prefix <= 40) return s.substring(0, 10);
  }
  // Hex NUID starting with 832b or 8317 → prepend 0000
  if (/^(832[bB]|8317)/i.test(s)) return '0000' + s;
  return s;
}

export default function ScanPage() {
  const navigate                    = useNavigate();
  const [mode, setMode]             = useState('idle'); // idle | scanning | manual | ocr | found | notfound
  const [manualSerial, setManual]   = useState('');
  const [scannedRaw, setScannedRaw] = useState('');
  const [scannedNorm, setScannedNorm] = useState('');
  const [ocrImage, setOcrImage]     = useState(null);   // data URL of captured photo
  const [ocrCandidates, setOcrCandidates] = useState([]); // extracted NUID options
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const ocrInputRef                 = useRef(null);
  const [customer, setCustomer]     = useState(null);
  const [loading, setLoading]       = useState(false);
  const [editing, setEditing]       = useState(false);
  const [editForm, setEditForm]     = useState({});
  const [payMode, setPayMode]       = useState(null); // null | cash | upi
  const [selectedUpi, setSelUpi]    = useState(1);   // 1 or 2
  const [baseAmount, setBaseAmount] = useState('');
  const [config, setConfig]         = useState({ upiId: '', upiId2: '', upiName: 'JagdishNet', upiDiscountPercent: 5 });
  const [paying, setPaying]         = useState(false);
  const scannerRef                  = useRef(null);

  useEffect(() => {
    configAPI.get().then(r => {
      if (r.data?.data) setConfig(r.data.data);
    }).catch(() => {});
  }, []);

  // ── Amounts ───────────────────────────────────────────────────────────────────
  const discountPct   = config.upiDiscountPercent || 0;
  const cashAmount    = parseFloat(baseAmount) || 0;
  const upiAmount     = cashAmount > 0 ? Math.round(cashAmount * (1 - discountPct / 100)) : 0;
  const savings       = cashAmount - upiAmount;
  const activeUpiId   = selectedUpi === 1 ? config.upiId : config.upiId2;

  const makeUpiLink = (upiId, amt, ref) =>
    `upi://pay?pa=${upiId}&pn=${encodeURIComponent(config.upiName)}&am=${amt}&cu=INR&tn=${encodeURIComponent(`CATV Renewal - ${ref}`)}`;

  // ── Scanner ────────────────────────────────────────────────────────────────────
  const startScan = async () => {
    setMode('scanning');
    setCustomer(null);
    await new Promise(r => setTimeout(r, 100));
    try {
      const scanner = new Html5Qrcode(SCANNER_ID);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 280, height: 120 } },
        (decoded) => { handleSerial(decoded.trim()); stopScan(); },
        () => {}
      );
    } catch {
      toast.error('Camera unavailable — use manual entry');
      setMode('manual');
    }
  };

  const stopScan = async () => {
    try { if (scannerRef.current) { await scannerRef.current.stop(); scannerRef.current = null; } } catch {}
  };

  useEffect(() => () => { stopScan(); }, []);

  // ── OCR from TV Screen ────────────────────────────────────────────────────────
  const handleOcrCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const url = URL.createObjectURL(file);
    setOcrImage(url);
    setOcrCandidates([]);
    setOcrProcessing(true);
    setMode('ocr');

    try {
      const { createWorker } = await import('tesseract.js');
      const worker = await createWorker('eng', 1, {
        logger: () => {},
        workerPath: undefined,
        langPath: undefined,
        corePath: undefined,
      });
      await worker.setParameters({ tessedit_char_whitelist: '0123456789abcdefABCDEF ' });
      const { data: { text } } = await worker.recognize(file);
      await worker.terminate();

      const candidates = extractNUIDCandidates(text);
      if (candidates.length === 0) {
        toast.error('No numbers found in image — try manual entry');
        setMode('manual');
      } else {
        setOcrCandidates(candidates);
      }
    } catch (err) {
      toast.error('OCR failed — try manual entry');
      setMode('manual');
    } finally {
      setOcrProcessing(false);
      URL.revokeObjectURL(url);
    }
  };

  // ── Lookup ─────────────────────────────────────────────────────────────────────
  const handleSerial = async (serial) => {
    if (!serial) return;
    const raw = serial.trim();
    const norm = normalizeNUID(raw);
    setScannedRaw(raw);
    setScannedNorm(norm);
    setLoading(true);
    setMode('idle');
    try {
      const res = await customerAPI.getByStb(norm);
      const c   = res.data.data;
      setCustomer(c);
      setEditForm({
        name: c.name, mobile: c.mobile,
        alternateMobile: c.alternateMobile || '',
        address: c.address?.line1 || '',
        area: c.address?.area || '',
        city: c.address?.city || '',
        notes: c.notes || '',
        catvExpiry: c.catv?.expiryDate ? new Date(c.catv.expiryDate).toISOString().slice(0, 10) : '',
      });
      const price = c.catv?.package?.price || c.broadband?.plan?.price || '';
      setBaseAmount(String(price));
      setMode('found');
      setPayMode(null);
    } catch (err) {
      if (err.response?.status === 404) {
        toast('New STB — redirecting to add customer...', { icon: '📋' });
        navigate(`/customers/add?stb=${encodeURIComponent(norm)}`);
      } else {
        setMode('notfound');
        toast.error(err.response?.data?.message || 'Lookup failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────────
  const saveEdit = async () => {
    try {
      const payload = {
        name: editForm.name, mobile: editForm.mobile,
        alternateMobile: editForm.alternateMobile,
        address: { line1: editForm.address, area: editForm.area, city: editForm.city },
        notes: editForm.notes,
      };
      if (editForm.catvExpiry) payload['catv.expiryDate'] = editForm.catvExpiry;
      await customerAPI.update(customer._id, payload);
      setCustomer(prev => ({
        ...prev, name: editForm.name, mobile: editForm.mobile,
        alternateMobile: editForm.alternateMobile,
        address: { ...prev.address, line1: editForm.address, area: editForm.area, city: editForm.city },
        notes: editForm.notes,
        catv: prev.catv ? { ...prev.catv, expiryDate: editForm.catvExpiry || prev.catv.expiryDate } : prev.catv,
      }));
      setEditing(false);
      toast.success('Customer updated');
    } catch { toast.error('Update failed'); }
  };

  // ── Payment ────────────────────────────────────────────────────────────────────
  const collectPayment = async (method) => {
    const amt = method === 'upi' ? upiAmount : cashAmount;
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setPaying(true);
    try {
      await paymentAPI.create({
        customer: customer._id,
        amount: amt,
        paymentMethod: method,
        paymentFor: (customer.customerType === 'catv' || customer.customerType === 'both')
          ? 'catv_renewal' : 'broadband_renewal',
      });
      toast.success(`₹${amt} ${method.toUpperCase()} payment recorded!`);
      setPayMode(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  const reset = () => {
    stopScan();
    setMode('idle'); setCustomer(null); setManual('');
    setEditing(false); setPayMode(null); setBaseAmount('');
    setScannedRaw(''); setScannedNorm('');
    setOcrImage(null); setOcrCandidates([]); setOcrProcessing(false);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
  const expiryInfo = (d) => {
    if (!d) return null;
    const days = Math.ceil((new Date(d) - new Date()) / 86400000);
    if (days < 0)  return { label: `Expired ${Math.abs(days)}d ago`, color: '#ef4444' };
    if (days <= 7) return { label: `Expires in ${days}d`, color: '#f97316' };
    return           { label: `Active — ${days}d left`, color: '#16a34a' };
  };

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '16px 12px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <Tv size={22} color="var(--primary)" />
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Scan STB & Collect</h2>
        {mode !== 'idle' && !loading && (
          <button onClick={reset} style={iconBtn}><X size={20} /></button>
        )}
      </div>

      {/* Hidden file input for OCR photo capture */}
      <input ref={ocrInputRef} type="file" accept="image/*" capture="environment"
        style={{ display: 'none' }} onChange={handleOcrCapture} />

      {/* Idle */}
      {mode === 'idle' && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button onClick={startScan} style={btn('#4f46e5')}>
            <Camera size={18} /> Scan STB Barcode (Camera)
          </button>
          <button onClick={() => ocrInputRef.current?.click()} style={btn('#0d9488')}>
            <ScanText size={18} /> Read NUID from TV Screen (Photo)
          </button>
          <button onClick={() => setMode('manual')} style={btn('#0891b2')}>
            <Search size={18} /> Enter NUID Manually
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Looking up customer...</div>}

      {/* Camera */}
      {mode === 'scanning' && (
        <div>
          <p style={{ color: '#555', fontSize: '.9rem', marginBottom: 10 }}>Point camera at the STB barcode</p>
          <div id={SCANNER_ID} style={{ width: '100%', borderRadius: 10, overflow: 'hidden' }} />
          <button onClick={() => { stopScan(); setMode('idle'); }} style={{ ...btn('#888'), marginTop: 12 }}>Cancel</button>
        </div>
      )}

      {/* Manual */}
      {mode === 'manual' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ fontWeight: 600, fontSize: '.9rem' }}>Enter NUID Serial (from STB sticker)</label>
          <input autoFocus value={manualSerial} onChange={e => setManual(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSerial(manualSerial)}
            placeholder="e.g. 3435522100" style={input} />
          {manualSerial && normalizeNUID(manualSerial) !== manualSerial.trim() && (
            <div style={{ fontSize: '.8rem', padding: '6px 10px', background: '#fef9c3', borderRadius: 6, border: '1px solid #fde047', color: '#713f12' }}>
              Auto-corrected: <strong>{normalizeNUID(manualSerial)}</strong>
            </div>
          )}
          <button onClick={() => handleSerial(manualSerial)} style={btn('#4f46e5')} disabled={!manualSerial}>
            <Search size={16} /> Find Customer
          </button>
        </div>
      )}

      {/* Customer found */}
      {mode === 'found' && customer && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* OCR mode */}
      {mode === 'ocr' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {ocrProcessing && (
            <div style={{ textAlign: 'center', padding: 30, color: '#555' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />
              <p style={{ fontSize: '.9rem' }}>Reading numbers from image...</p>
            </div>
          )}
          {!ocrProcessing && ocrCandidates.length > 0 && (
            <div>
              <p style={{ fontWeight: 600, fontSize: '.9rem', marginBottom: 10 }}>
                Numbers found — tap the NUID shown on TV screen:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {ocrCandidates.map(n => {
                  const norm = normalizeNUID(n);
                  return (
                    <button key={n} onClick={() => handleSerial(n)}
                      style={{ ...btn('#4f46e5'), flexDirection: 'column', alignItems: 'flex-start', padding: '12px 16px' }}>
                      <span style={{ fontSize: '1rem', fontFamily: 'monospace', letterSpacing: 1 }}>{n}</span>
                      {norm !== n && (
                        <span style={{ fontSize: '.72rem', opacity: .8, marginTop: 2 }}>→ will use: {norm}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p style={{ fontSize: '.78rem', color: '#888', marginTop: 10 }}>
                Not listed? <button onClick={() => setMode('manual')} style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: '.78rem', padding: 0 }}>Enter manually</button>
              </p>
            </div>
          )}
        </div>
      )}

      {/* NUID normalization info */}
          {scannedRaw && scannedRaw !== scannedNorm && (
            <div style={{ fontSize: '.8rem', padding: '7px 12px', background: '#fef9c3', borderRadius: 8, border: '1px solid #fde047', color: '#713f12' }}>
              Scanned: <code>{scannedRaw}</code> → Used: <strong>{scannedNorm}</strong>
            </div>
          )}

          {/* Customer card */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>{customer.name}</h3>
              <button onClick={() => setEditing(!editing)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5', padding: 4 }}>
                <Edit2 size={16} />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 10, fontSize: '.87rem', color: '#444' }}>
              <span><Phone size={13} style={ico} />{customer.mobile || '—'}</span>
              <span><MapPin size={13} style={ico} />
                {[customer.address?.line1, customer.address?.area, customer.address?.city].filter(Boolean).join(', ') || '—'}
              </span>
              <span><Tv size={13} style={ico} />STB: {customer.catv?.setTopBoxSerial || '—'}</span>
              {customer.catv?.package && (
                <span style={{ fontWeight: 600, color: '#4f46e5' }}>
                  {customer.catv.package.displayName} — ₹{customer.catv.package.price}/mo
                </span>
              )}
              {customer.catv?.expiryDate && (() => {
                const s = expiryInfo(customer.catv.expiryDate);
                return s ? <span style={{ fontWeight: 600, color: s.color }}>{s.label}</span> : null;
              })()}
              <span><Calendar size={13} style={ico} />Expiry: {fmtDate(customer.catv?.expiryDate)}</span>
            </div>
          </div>

          {/* Edit form */}
          {editing && (
            <div style={{ ...card, background: '#f8faff', border: '1px solid #c7d2fe' }}>
              <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: '.9rem' }}>Edit Customer Details</p>
              {[
                { label: 'Name', key: 'name' }, { label: 'Mobile', key: 'mobile' },
                { label: 'Alt Mobile', key: 'alternateMobile' }, { label: 'Address', key: 'address' },
                { label: 'Area', key: 'area' }, { label: 'City', key: 'city' }, { label: 'Notes', key: 'notes' },
              ].map(({ label, key }) => (
                <div key={key} style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: '.8rem', color: '#666' }}>{label}</label>
                  <input value={editForm[key] || ''} onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))} style={{ ...input, marginTop: 2 }} />
                </div>
              ))}
              {customer.catv?.expiryDate !== undefined && (
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: '.8rem', color: '#666' }}>Due Date (CATV Expiry)</label>
                  <input type="date" value={editForm.catvExpiry || ''} onChange={e => setEditForm(f => ({ ...f, catvExpiry: e.target.value }))} style={{ ...input, marginTop: 2 }} />
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button onClick={saveEdit} style={{ ...btn('#16a34a'), flex: 1, padding: 10 }}><Check size={15} /> Save</button>
                <button onClick={() => setEditing(false)} style={{ ...btn('#888'), flex: 1, padding: 10 }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Payment section */}
          {!editing && (
            <div style={card}>
              <p style={{ margin: '0 0 12px', fontWeight: 600, fontSize: '.95rem' }}>Collect Payment</p>

              {/* Amount input */}
              <label style={{ fontSize: '.85rem', color: '#555' }}>Plan Amount (₹)</label>
              <input type="number" value={baseAmount} onChange={e => setBaseAmount(e.target.value)}
                placeholder="Enter amount" style={{ ...input, marginTop: 4, fontSize: '1.1rem', fontWeight: 600 }} />

              {/* Discount badge */}
              {discountPct > 0 && cashAmount > 0 && (
                <div style={{ display: 'flex', gap: 10, marginTop: 10, padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0', fontSize: '.85rem' }}>
                  <Tag size={14} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    <strong style={{ color: '#16a34a' }}>{discountPct}% UPI discount</strong>
                    {' '}— Customer pays <strong>₹{upiAmount}</strong> instead of ₹{cashAmount}
                    {' '}(saves ₹{savings})
                  </span>
                </div>
              )}

              {/* Mode buttons */}
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button onClick={() => setPayMode(payMode === 'cash' ? null : 'cash')}
                  style={{ ...btn(payMode === 'cash' ? '#15803d' : '#16a34a'), flex: 1 }}>
                  <Banknote size={16} /> Cash ₹{cashAmount || '—'}
                </button>
                <button onClick={() => setPayMode(payMode === 'upi' ? null : 'upi')}
                  style={{ ...btn(payMode === 'upi' ? '#1d4ed8' : '#2563eb'), flex: 1 }}>
                  <CreditCard size={16} /> UPI ₹{upiAmount || '—'}
                </button>
              </div>

              {/* Cash */}
              {payMode === 'cash' && (
                <div style={{ marginTop: 14, padding: '14px', background: '#f9fafb', borderRadius: 10, textAlign: 'center' }}>
                  <p style={{ margin: '0 0 10px', color: '#555', fontSize: '.9rem' }}>
                    Collecting <strong>₹{cashAmount}</strong> cash from <strong>{customer.name}</strong>
                  </p>
                  <button onClick={() => collectPayment('cash')} disabled={paying} style={btn('#15803d')}>
                    {paying ? 'Recording...' : '✓ Confirm Cash Received'}
                  </button>
                </div>
              )}

              {/* UPI */}
              {payMode === 'upi' && (
                <div style={{ marginTop: 14 }}>
                  {/* UPI account selector */}
                  {config.upiId2 && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                      {[1, 2].map(n => (
                        <button key={n} onClick={() => setSelUpi(n)}
                          style={{ flex: 1, padding: '8px', borderRadius: 8, border: `2px solid ${selectedUpi === n ? '#2563eb' : '#d1d5db'}`,
                            background: selectedUpi === n ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '.78rem', fontWeight: 600 }}>
                          {n === 1 ? 'HDFC Bank' : 'ICICI Bank'}
                          <br /><span style={{ fontSize: '.7rem', fontWeight: 400, color: '#666' }}>{n === 1 ? config.upiId : config.upiId2}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {activeUpiId ? (
                    <div style={{ textAlign: 'center' }}>
                      {/* Discount highlight */}
                      {discountPct > 0 && (
                        <div style={{ marginBottom: 10, padding: '6px 12px', background: '#f0fdf4', borderRadius: 8, display: 'inline-block', fontSize: '.85rem', color: '#15803d', fontWeight: 600 }}>
                          🎉 {discountPct}% Discount Applied — Customer pays ₹{upiAmount}
                        </div>
                      )}
                      <p style={{ color: '#555', fontSize: '.85rem', marginBottom: 10 }}>
                        Customer scans this QR with any UPI app
                      </p>
                      <div style={{ background: '#fff', padding: 16, borderRadius: 12, display: 'inline-block', border: '2px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
                        <QRCode value={makeUpiLink(activeUpiId, upiAmount, customer.customerId || customer._id)} size={210} />
                        <div style={{ marginTop: 8, fontSize: '.75rem', color: '#888' }}>
                          {config.upiName} · ₹{upiAmount} · UPI
                        </div>
                      </div>
                      <p style={{ fontSize: '.75rem', color: '#888', marginTop: 6 }}>
                        Amount ₹{upiAmount} is pre-filled · UPI: {activeUpiId}
                      </p>
                      <div style={{ marginTop: 12, padding: '10px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a', fontSize: '.83rem', color: '#92400e' }}>
                        ⚠️ After customer pays, tap the button below to record it
                      </div>
                      <button onClick={() => collectPayment('upi')} disabled={paying} style={{ ...btn('#2563eb'), marginTop: 10 }}>
                        {paying ? 'Recording...' : '✓ Customer Paid — Record It'}
                      </button>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 20, color: '#888', fontSize: '.9rem' }}>
                      <AlertCircle size={24} style={{ marginBottom: 8 }} />
                      <p>UPI ID not configured. Contact admin.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <button onClick={reset} style={{ ...btn('#6b7280'), padding: '10px' }}>← Scan Another</button>
        </div>
      )}

      {/* Info box — auto-payment note */}
      {mode === 'idle' && (
        <div style={{ marginTop: 24, padding: '12px 14px', background: '#f0f9ff', borderRadius: 10, border: '1px solid #bae6fd', fontSize: '.82rem', color: '#0369a1' }}>
          <strong>Tip:</strong> Share the UPI QR with customers via WhatsApp so they can pay from home without staff visit. Staff still confirms payment in the app after customer pays.
        </div>
      )}
    </div>
  );
}

const btn  = (bg) => ({ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: bg, color: '#fff', border: 'none', borderRadius: 10, padding: '13px 16px', fontSize: '.95rem', fontWeight: 600, cursor: 'pointer', width: '100%' });
const input = { width: '100%', padding: '10px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: '.95rem', boxSizing: 'border-box' };
const card  = { background: '#fff', borderRadius: 12, padding: '16px', boxShadow: '0 1px 8px rgba(0,0,0,.08)' };
const iconBtn = { marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#888' };
const ico   = { marginRight: 6, verticalAlign: 'middle' };
